import { LOCALSTORAGE_KEYS, PIECE_STATUS } from 'src/utils/constants';
import routes from 'src/utils/routes';
import { getRouteURI, getUuid } from 'src/utils/url';

export const PIECE_SCAN_VIEW_OPTIONS = {
  RUN: 'run',
  PIECE: 'piece',
};

const DEFAULT_PREFERENCE = {
  remember: false,
  view: PIECE_SCAN_VIEW_OPTIONS.PIECE,
};

const getStorageKey = user => {
  if (!user?.uri) {
    return null;
  }

  return `${LOCALSTORAGE_KEYS.PIECE_SCAN_VIEW_PREFERENCE}:${getUuid(user.uri)}`;
};

const normalizePreference = preference => ({
  remember: Boolean(preference?.remember),
  view:
    preference?.view === PIECE_SCAN_VIEW_OPTIONS.RUN
      ? PIECE_SCAN_VIEW_OPTIONS.RUN
      : PIECE_SCAN_VIEW_OPTIONS.PIECE,
});

export const getPieceScanViewPreference = user => {
  const key = getStorageKey(user);

  if (!key || typeof window === 'undefined') {
    return DEFAULT_PREFERENCE;
  }

  try {
    const stored = window.localStorage.getItem(key);

    if (stored) {
      return normalizePreference(JSON.parse(stored));
    }
  } catch (error) {
    console.error(error);
  }

  return DEFAULT_PREFERENCE;
};

export const setPieceScanViewPreference = (user, preference) => {
  const key = getStorageKey(user);

  if (!key || typeof window === 'undefined') {
    return;
  }

  const normalized = normalizePreference(preference);

  if (!normalized.remember) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(normalized));
};

export const isFinishedPieceStatus = status =>
  status === PIECE_STATUS.COMPLETE || status === PIECE_STATUS.INCOMPLETE;

export const getPieceOverviewPath = pieceUuid =>
  getRouteURI(routes.pieceOverview, { uuid: pieceUuid });

export const getPieceRunPath = ({ pieceUuid, runUuid }) => {
  if (!runUuid) {
    return null;
  }

  return `${getRouteURI(routes.run, { uuid: runUuid })}?scannedPiece=${pieceUuid}`;
};

export const getPieceScanViewPath = ({ view, pieceUuid, runUuid }) => {
  if (view === PIECE_SCAN_VIEW_OPTIONS.RUN) {
    return getPieceRunPath({ pieceUuid, runUuid });
  }

  return getPieceOverviewPath(pieceUuid);
};

export const getPieceLandingPath = printUri => {
  if (!printUri) {
    return null;
  }

  return getRouteURI(routes.landing, { print: getUuid(printUri) });
};

export const resolvePieceScanDestination = async ({ piece, pieceUuid, user, api }) => {
  if (isFinishedPieceStatus(piece.status) || !piece.current_print) {
    return getPieceOverviewPath(pieceUuid);
  }

  const preference = getPieceScanViewPreference(user);

  if (preference.remember) {
    if (preference.view === PIECE_SCAN_VIEW_OPTIONS.PIECE) {
      return getPieceOverviewPath(pieceUuid);
    }

    if (preference.view === PIECE_SCAN_VIEW_OPTIONS.RUN && piece.current_print) {
      const currentPrint = await api.get(piece.current_print, { prefixUrl: false }).json();
      const runPath = getPieceRunPath({
        pieceUuid,
        runUuid: currentPrint?.run ? getUuid(currentPrint.run) : null,
      });

      if (runPath) {
        return runPath;
      }
    }
  }

  return getPieceLandingPath(piece.current_print);
};
